import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Link2, RefreshCw, UserCheck, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const BiometricMappingModal = ({
  open, onOpenChange,
  selectedRegistrationNumber,
  employeeSearchTerm, setEmployeeSearchTerm,
  employeeSearchResults,
  selectedEmployee, setSelectedEmployee,
  searchEmployees,
  mapping, handleManualMapping, resetMappingForm,
}) => {
  const { t } = useTranslation();
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t('biometricComp.mappingTitle')}</DialogTitle>
        <DialogDescription>
          {t('biometricComp.mappingDesc', { num: selectedRegistrationNumber })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4">
        <div className="space-y-2">
          <Label htmlFor="employee-search">{t('biometricComp.searchEmp')}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              id="employee-search"
              placeholder={t('biometricComp.searchPlaceholder')}
              value={employeeSearchTerm}
              onChange={(e) => {
                setEmployeeSearchTerm(e.target.value);
                searchEmployees(e.target.value);
              }}
              className="pl-10"
            />
          </div>
        </div>

        {employeeSearchResults.length > 0 && (
          <div className="space-y-2">
            <Label>{t('biometricComp.searchResults')}</Label>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {employeeSearchResults.map((employee) => (
                <div
                  key={employee.id}
                  className={`p-3 border-b cursor-pointer hover:bg-accent ${
                    selectedEmployee?.id === employee.id ? 'bg-primary/5 border-primary/30' : ''
                  }`}
                  onClick={() => setSelectedEmployee(employee)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{employee.full_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {employee.email} • {employee.registration_number}
                      </div>
                      {employee.department_name && (
                        <div className="text-xs text-gray-400">
                          {employee.department_name} • {employee.job_title}
                        </div>
                      )}
                    </div>
                    {selectedEmployee?.id === employee.id && <CheckCircle className="h-5 w-5 text-green-500" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedEmployee && (
          <div className="p-4 bg-green-950/20 border border-green-900/40 rounded-lg">
            <div className="flex items-center space-x-2">
              <UserCheck className="h-5 w-5 text-green-400" />
              <div>
                <div className="font-medium text-foreground">{t('biometricComp.empSelected')}</div>
                <div className="text-sm text-muted-foreground">
                  {selectedEmployee.full_name} ({selectedEmployee.registration_number})
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={() => { onOpenChange(false); resetMappingForm(); }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleManualMapping}
            disabled={!selectedEmployee || !selectedRegistrationNumber || mapping}
            className="bg-green-600 hover:bg-green-700"
          >
            {mapping ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t('biometricComp.mappingInProgress')}</>
            ) : (
              <><Link2 className="h-4 w-4 mr-2" />{t('biometricComp.confirmMapping')}</>
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default BiometricMappingModal;
